import { useEffect, useRef } from 'react';
import StabilityMeter from './controls/StabilityMeter.jsx';
import LifeBoard3D from './canvas/LifeBoard3D.jsx';
import EventFeedback from './events/EventFeedback.jsx';

/**
 * MicroView - Single life board experience
 * 
 * Shows:
 * - Isometric life board (3D canvas)
 * - 4 zones (Home, Auto, Health, Liability)
 * - Behavioral controls
 * - Stability meter
 * - Queued events (telegraphed)
 */
export default function MicroView({
  life,
  queuedEvents,
  onBehaviorChange,
  onCoverageChange,
  onLifestyleChange,
  onInjectEvent,
  isPaused
}) {
  if (!life) {
    return <div className="flex items-center justify-center h-screen">No life data</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Entry text - only shown briefly */}
      {life.time < 5 && (
        <div className="text-center mb-8 animate-fade-in">
          <p className="text-2xl text-gray-400 font-light tracking-wide">
            A life in motion.
          </p>
        </div>
      )}

      {/* Main life board - 3D Canvas */}
      <div className="mb-8">
        <LifeBoard3D
          life={life}
          queuedEvents={queuedEvents}
          isPaused={isPaused}
        />
      </div>

      {/* Stability Meter - Primary feedback */}
      <div className="mb-8">
        <StabilityMeter
          stability={life.stabilityMeter}
          recoveryBandwidth={life.recoveryBandwidth}
        />
      </div>

      {/* Zone Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Home Zone */}
        <ZoneCard
          title="Home"
          icon="🏠"
          zone={life.zones.home}
          onBehaviorChange={(param, value) => onBehaviorChange('home', param, value)}
          onCoverageChange={(level) => onCoverageChange('home', level)}
          activeEvents={queuedEvents.filter(e => e.zone === 'home')}
        />

        {/* Auto Zone */}
        <ZoneCard
          title="Auto"
          icon="🚗"
          zone={life.zones.auto}
          onBehaviorChange={(param, value) => onBehaviorChange('auto', param, value)}
          onCoverageChange={(level) => onCoverageChange('auto', level)}
          activeEvents={queuedEvents.filter(e => e.zone === 'auto')}
        />

        {/* Health Zone */}
        <ZoneCard
          title="Health"
          icon="💚"
          zone={life.zones.health}
          onBehaviorChange={(param, value) => onBehaviorChange('health', param, value)}
          onCoverageChange={(level) => onCoverageChange('health', level)}
          activeEvents={queuedEvents.filter(e => e.zone === 'health')}
        />

        {/* Liability Zone */}
        <ZoneCard
          title="Liability"
          icon="⚖️"
          zone={life.zones.liability}
          onBehaviorChange={(param, value) => onBehaviorChange('liability', param, value)}
          onCoverageChange={(level) => onCoverageChange('liability', level)}
          activeEvents={queuedEvents.filter(e => e.zone === 'liability')}
        />
      </div>

      {/* Lifestyle Controls */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-8">
        <h3 className="text-lg font-mono mb-4 text-gray-300">Lifestyle</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SliderControl
            label="Commute Length"
            value={life.lifestyle.commuteLength}
            onChange={(value) => onLifestyleChange('commuteLength', value)}
            min="Short"
            max="Long"
          />
          <SliderControl
            label="Living Density"
            value={life.lifestyle.livingDensity}
            onChange={(value) => onLifestyleChange('livingDensity', value)}
            min="Rural"
            max="Urban"
          />
          <SliderControl
            label="Work Stress"
            value={life.lifestyle.workStress}
            onChange={(value) => onLifestyleChange('workStress', value)}
            min="Low"
            max="High"
          />
          <SliderControl
            label="Environment Exposure"
            value={life.lifestyle.environmentExposure}
            onChange={(value) => onLifestyleChange('environmentExposure', value)}
            min="Protected"
            max="Exposed"
          />
        </div>
      </div>

      {/* Event Feedback - Post-event analysis */}
      <EventFeedback life={life} />

      {/* Cost Accumulation - Subtle */}
      <div className="text-center text-sm text-gray-600 font-mono mt-8">
        System Cost: ${Math.floor(life.totalCostAccumulation).toLocaleString()}
      </div>
    </div>
  );
}

// Zone Card Component
function ZoneCard({ title, icon, zone, onBehaviorChange, onCoverageChange, activeEvents }) {
  const conditionColor = zone.condition > 70 ? 'bg-green-500' : zone.condition > 40 ? 'bg-yellow-500' : 'bg-red-500';
  
  return (
    <div className="bg-white/5 rounded-xl p-6 border border-white/10 backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{icon}</span>
          <h3 className="text-xl font-mono">{title}</h3>
        </div>
        <div className="text-right">
          <div className="text-2xl font-mono font-bold">{Math.floor(zone.condition)}%</div>
          <div className="text-xs text-gray-500">Condition</div>
        </div>
      </div>

      {/* Condition Bar */}
      <div className="mb-4">
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full ${conditionColor} transition-all duration-500`}
            style={{ width: `${zone.condition}%` }}
          />
        </div>
      </div>

      {/* Active Events - Telegraphed */}
      {activeEvents.length > 0 && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          {activeEvents.map(event => (
            <div key={event.id} className="flex items-center justify-between text-sm">
              <span className="text-red-300">{event.name} incoming</span>
              <span className="text-red-400 font-mono">{event.timeUntilImpact.toFixed(1)}s</span>
            </div>
          ))}
        </div>
      )}

      {/* Behavioral Controls */}
      <div className="space-y-3">
        {getBehavioralControls(title, zone, onBehaviorChange)}
        
        {/* Coverage Control */}
        <SliderControl
          label="Coverage"
          value={zone.coverage}
          onChange={onCoverageChange}
          min="None"
          max="Full"
          color="blue"
        />
      </div>
    </div>
  );
}

// Get zone-specific behavioral controls
function getBehavioralControls(zoneTitle, zone, onChange) {
  if (zoneTitle === 'Home') {
    return (
      <SliderControl
        label="Maintenance"
        value={zone.maintenanceLevel}
        onChange={(value) => onChange('maintenanceLevel', value)}
        min="Deferred"
        max="Proactive"
      />
    );
  } else if (zoneTitle === 'Auto') {
    return (
      <SliderControl
        label="Driving Style"
        value={zone.drivingStyle}
        onChange={(value) => onChange('drivingStyle', value)}
        min="Defensive"
        max="Aggressive"
      />
    );
  } else if (zoneTitle === 'Health') {
    return (
      <SliderControl
        label="Preventative Care"
        value={zone.preventativeCare}
        onChange={(value) => onChange('preventativeCare', value)}
        min="Reactive"
        max="Proactive"
      />
    );
  }
  return null;
}

// Reusable Slider Control
function SliderControl({ label, value, onChange, min, max, color = 'white' }) {
  const colorClasses = {
    white: 'bg-white',
    blue: 'bg-blue-500',
    green: 'bg-green-500'
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-gray-400 font-mono">{label}</label>
        <span className="text-xs text-gray-500">{Math.floor(value * 100)}%</span>
      </div>
      <div className="relative">
        <input
          type="range"
          min="0"
          max="100"
          value={value * 100}
          onChange={(e) => onChange(parseFloat(e.target.value) / 100)}
          className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer slider"
          style={{
            background: `linear-gradient(to right, ${color === 'white' ? 'rgba(255,255,255,0.3)' : 'rgba(59,130,246,0.3)'} 0%, ${color === 'white' ? 'rgba(255,255,255,0.3)' : 'rgba(59,130,246,0.3)'} ${value * 100}%, rgba(255,255,255,0.1) ${value * 100}%, rgba(255,255,255,0.1) 100%)`
          }}
        />
        <div className="flex justify-between text-xs text-gray-600 mt-1">
          <span>{min}</span>
          <span>{max}</span>
        </div>
      </div>
    </div>
  );
}

